import { css } from '@emotion/react';
import React, { useState } from 'react';
import { A11y, Navigation, Pagination, Scrollbar } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import leftButtonImage from '@/ui/resources/svg/button-carousel-left.svg';
import rightButtonImage from '@/ui/resources/svg/button-carousel-right.svg';
import { useTheme } from '@/ui/theme';
import { BodySmall } from '@/ui/typography';

import { Lightbox } from './lightbox';

type ButtonDirection = 'left' | 'right';

export const getNavigationButtonImage = (direction: ButtonDirection) => {
  switch (direction) {
    case 'left':
      return leftButtonImage.src;
    case 'right':
      return rightButtonImage.src;
  }
};

interface ContentSliderProps {
  children: React.ReactNode[];
  lightboxItems?: {
    src: string;
    alt: string;
    width: number;
    height: number;
  }[];
}

export const ContentSlider = (props: ContentSliderProps) => {
  const [isLightboxOpened, setIsLightboxOpened] = useState(false);
  const [selectedSlideIndex, setSelectedSlideIndex] = useState(0);
  const {
    colors,
    componentProperties: { imageSlider },
  } = useTheme();

  const navigationStyles = css`
    .swiper-button-next,
    .swiper-button-prev {
      border-radius: 100%;
      width: 40px;
      height: 40px;
      border: 1px solid ${colors.secondary100};
      background-color: #fff;
      opacity: 0.5;
      will-change: opacity;
      transition: opacity 0.3s ease, box-shadow 0.3s ease;
      box-shadow: none;

      &:focus,
      &:hover {
        opacity: 1;
        box-shadow: 1px 1px 2px 0 rgba(0, 0, 0, 0.3);

        &.swiper-button-disabled {
          opacity: 0;
        }
      }

      &:after {
        width: 20px;
        height: 20px;
        position: relative;
      }
    }

    .swiper-button-next:after {
      background: url(${getNavigationButtonImage('right')}) center;
      inset-inline-start: 1px;
      [dir='rtl'] & {
        background: url(${getNavigationButtonImage('left')}) center;
      }
    }
    .swiper-button-prev:after {
      background: url(${getNavigationButtonImage('left')}) center;
      inset-inline-start: -1px;
      [dir='rtl'] & {
        background: url(${getNavigationButtonImage('right')}) center;
      }
    }

    .swiper-button-disabled {
      opacity: 0;
    }
  `;
  const paginationStyles = css`
    .swiper-pagination {
      bottom: -36px;
      text-align: left;
      span {
        ${imageSlider?.paginator?.color &&
        `color: ${imageSlider?.paginator?.color};`}
      }
    }

    .swiper-pagination-fraction {
      display: flex;
      justify-content: flex-start;

      /**
             * Offset this for mobile search view
             */
      @media all and (max-width: 639px) {
        padding-left: 28px;
      }

      .swiper-pagination-line {
        width: 16px;
        height: 1px;
        background: ${imageSlider?.paginator?.color ?? colors.textSecondary};
        display: inline-block;
        margin: ${imageSlider?.paginator?.line?.margin ?? '12px 10px 0 10px'};
      }

      .swiper-pagination-total {
        color: ${imageSlider?.paginator?.color ?? colors.textSecondary};
      }
    }
  `;
  const notificationStyles = css`
    .swiper-notification {
      position: absolute;
      inset-inline-end: 9999px;
    }
  `;

  return (
    <div
      css={[
        {
          // Make this wrapper component the anchor by position: relative, so that we can pop out elements from inside the swiper-container (which is overflow: hidden)
          position: 'relative',
          marginBottom: '30px',
          '.swiper': {
            position: 'static',
          },
        },
        navigationStyles,
        paginationStyles,
        notificationStyles,
      ]}
    >
      <BodySmall>
        <Swiper
          modules={[Navigation, Pagination, Scrollbar, A11y]}
          pagination={{
            clickable: true,
            type: 'fraction',
            renderFraction: (currentClass, totalClass) =>
              `<span class="${currentClass}"></span><span class="swiper-pagination-line"></span><span class="${totalClass}"></span>`,
          }}
          navigation
          onSlideChange={(swiper) => {
            setSelectedSlideIndex(swiper.activeIndex);
          }}
        >
          {props.children.map((item, key) => (
            <SwiperSlide
              key={key}
              onClick={
                props.lightboxItems
                  ? () => setIsLightboxOpened(true)
                  : undefined
              }
              style={{
                cursor: props.lightboxItems ? 'zoom-in' : undefined,
              }}
            >
              {item}
            </SwiperSlide>
          ))}
          {props.lightboxItems && (
            <button
              css={{
                position: 'absolute',
                right: 16,
                bottom: 12,
                zIndex: 1,
                ':focus': {
                  outline: '1px solid blue',
                },
                cursor: 'zoom-in',
              }}
              onClick={() => setIsLightboxOpened(true)}
            >
              <img src="/icons/expand.svg" alt="" />
            </button>
          )}
        </Swiper>
        {props.lightboxItems && (
          <Lightbox
            isOpen={isLightboxOpened}
            setIsOpen={setIsLightboxOpened}
            index={selectedSlideIndex}
          >
            {props.lightboxItems}
          </Lightbox>
        )}
      </BodySmall>
    </div>
  );
};
